'use client';

import { createContext, useContext } from 'react';
import type { ThemeDef } from '@/constants/themes';

/**
 * The app-theme state published by `ThemedShell` (providers.tsx). Carries the
 * mode-resolved preset + the active light/dark mode — the same hydration-gated
 * values the MUI theme used to hold, now framework-agnostic so `useThemeTokens`
 * no longer needs `@mui/material`.
 */
export interface AppThemeState {
  /** Preset already resolved for the active mode (see resolvePresetForMode). */
  preset: ThemeDef | null;
  mode: 'light' | 'dark';
}

export const AppThemeContext = createContext<AppThemeState>({ preset: null, mode: 'light' });

/**
 * Mode-aware semantic tokens used across pages.
 *
 * The codebase had ~80 files declaring local color constants like
 *   const PRIMARY = '#0f172a';
 *   const SURFACE = '#ffffff';
 *   const BORDER = 'rgba(226,232,240,0.9)';
 * which baked light-mode colors into the JSX. Dark mode now toggles the
 * MUI palette but those literals never moved, so pages stayed white.
 *
 * This hook returns the SAME names every page already uses, but resolved
 * against the current MUI theme. Refactoring a page becomes a one-line swap:
 *
 *   const PRIMARY = '#0f172a';            →   const t = useThemeTokens();
 *   ... bgcolor: SURFACE ...                  ... bgcolor: t.SURFACE ...
 *
 * When a theme PRESET is active (see src/constants/themes.ts), the neutral +
 * brand tokens below are sourced from the preset so every page that reads this
 * hook recolors app-wide. Semantic status colours (GREEN/RED/…) stay fixed —
 * they carry meaning, not brand.
 *
 * Keep the surface area small — only put tokens here that appear in ≥2
 * files. Page-specific accents stay local.
 */
export function useThemeTokens() {
  // Read the mode-resolved preset + mode from the app-theme context (published
  // by ThemedShell, already hydration-gated) so SSR and the first client render
  // agree → no mismatch.
  const { preset, mode } = useContext(AppThemeContext);
  const isDark = preset ? preset.mode === 'dark' : mode === 'dark';

  return {
    // Neutral chrome — preset-driven when a preset is active.
    PRIMARY:        preset ? preset.text       : (isDark ? '#f8fafc' : '#0f172a'),   // headings, primary actions
    PRIMARY_HOVER:  preset ? preset.muted      : (isDark ? '#cbd5e1' : '#1e293b'),
    SURFACE:        preset ? preset.surface    : (isDark ? '#0f172a' : '#ffffff'),   // page background blocks
    SURFACE_SOFT:   preset ? preset.surfaceAlt : (isDark ? 'rgba(30,41,59,0.6)' : 'rgba(248,250,252,0.6)'),
    BG:             preset ? preset.bg         : (isDark ? '#020617' : '#f8fafc'),   // app background
    PAPER:          preset ? preset.surface    : (isDark ? '#1e293b' : '#ffffff'),   // cards, paper
    BORDER:         preset ? preset.border     : (isDark ? 'rgba(51,65,85,0.9)' : 'rgba(226,232,240,0.9)'),
    BORDER_STRONG:  preset ? preset.border     : (isDark ? 'rgba(71,85,105,0.9)' : 'rgba(203,213,225,0.9)'),

    // Text scale
    TEXT:           preset ? preset.text       : (isDark ? '#f1f5f9' : '#0f172a'),
    MUTED:          preset ? preset.muted      : (isDark ? 'rgba(203,213,225,0.85)' : 'rgba(100,116,139,1)'),
    MUTED_SOFT:     preset ? preset.muted      : (isDark ? 'rgba(148,163,184,0.7)' : 'rgba(148,163,184,1)'),
    // Faintest ink — uppercase field labels, timeline meta. Below MUTED_SOFT.
    INK_FAINT:      preset ? preset.muted      : (isDark ? 'rgba(148,163,184,0.6)' : 'rgba(148,163,184,0.95)'),
    // Soft-filled control/well background (view→edit fields, insets).
    WASH:           preset ? preset.surfaceAlt : (isDark ? 'rgba(30,41,59,0.55)' : 'rgba(241,245,239,0.9)'),

    // Brand accent — preset brand colour, else BuilderSiteNow terracotta
    // (slightly lighter in dark for legibility).
    ACCENT:         preset ? preset.primary     : (isDark ? '#10B981' : '#047857'),
    ACCENT_HOVER:   preset ? preset.primaryDark : (isDark ? '#34D399' : '#065F46'),

    // Status tones — same hue both modes, lighter in dark.
    GREEN:          isDark ? '#34d399' : '#047857',
    AMBER:          isDark ? '#fbbf24' : '#b45309',
    RED:            isDark ? '#f87171' : '#b91c1c',
    DANGER:         isDark ? '#f87171' : '#b91c1c',
    BLUE:           isDark ? '#60a5fa' : '#2563eb',  // semantic "info" tone, kept blue on purpose
    VIOLET:         isDark ? '#a78bfa' : '#7c3aed',
    PURPLE:         isDark ? '#c084fc' : '#9333ea',
    WARN:           isDark ? '#fbbf24' : '#b45309',
    WARN_BG:        isDark ? 'rgba(251,191,36,0.15)' : 'rgba(251,191,36,0.1)',
    WARN_FG:        isDark ? '#fde68a' : '#92400e',

    // Status-row backgrounds (soft tint behind icons / pills).
    GREEN_BG:       isDark ? 'rgba(52,211,153,0.15)' : 'rgba(4,120,87,0.08)',
    AMBER_BG:       isDark ? 'rgba(251,191,36,0.15)' : 'rgba(180,83,9,0.08)',
    RED_BG:         isDark ? 'rgba(248,113,113,0.15)' : 'rgba(185,28,28,0.08)',
    BLUE_BG:        isDark ? 'rgba(96,165,250,0.15)' : 'rgba(37,99,235,0.08)',
    VIOLET_BG:      isDark ? 'rgba(167,139,250,0.15)' : 'rgba(124,58,237,0.08)',

    // Hover backdrops for clickable rows / icon buttons.
    HOVER:          isDark ? 'rgba(248,250,252,0.06)' : 'rgba(15,23,42,0.04)',
    HOVER_STRONG:   isDark ? 'rgba(248,250,252,0.1)'  : 'rgba(15,23,42,0.08)',

    isDark,
    mode,
  };
}

export type ThemeTokens = ReturnType<typeof useThemeTokens>;
